import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components } from './schema';

export type AnalysisType = components['schemas']['AnalysisType'];
export type AnalysisTypeBody = components['schemas']['AnalysisTypeRequest'];
export type AnalysisStaff = components['schemas']['AnalysisStaff'];
export type AnalysisInput = components['schemas']['AnalysisInput'];
export type Output = components['schemas']['Output'];
export type ChecklistRow = components['schemas']['ChecklistRow'];
export type AnalysesSummary = components['schemas']['AnalysesSummary'];
export type PlanAnalysisBody = components['schemas']['PlanAnalysisRequest'];
/** A re-run of a failed analysis or one sent back from review: the starting step, a version pin, overrides, why, and staff scheduling. */
export type RerunBody = components['schemas']['RerunRequest'];
/** What a contents-viewer gets: the staff shape minus internals. */
export type AnalysisClient = Pick<AnalysisStaff, 'public_id' | 'project' | 'name' | 'type_key' | 'client_status' | 'progress' | 'delivered_at' | 'outputs' | 'supersedes' | 'created_at'>;

export interface Slot {
  role: string;
  kind: string;
}
export interface OutputDecl {
  kind: string;
  deliverable: boolean;
}
export type InputSource = { slot: string } | { run: number; kind: string };
export interface Step {
  run_type: string;
  version: string | null;
  config: Record<string, unknown>;
  inputs: Record<string, InputSource>;
}

export const STAFF_ANALYSIS_STATES = ['planned', 'ready', 'running', 'failed', 'review', 'delivered', 'cancelled'] as const;
export const isProcessing = (s: string | undefined) => s === 'running' || s === 'failed' || s === 'review';

export async function listAnalysisTypes(): Promise<AnalysisType[]> {
  return unwrap(await api.GET('/api/analysis-types'));
}
export async function getAnalysisType(key: string): Promise<AnalysisType> {
  return unwrap(await api.GET('/api/analysis-types/{key}', { params: { path: { key } } }));
}
export async function createAnalysisType(body: AnalysisTypeBody): Promise<AnalysisType> {
  return unwrap(await api.POST('/api/analysis-types', { body }));
}
export async function updateAnalysisType(key: string, patch: Partial<AnalysisTypeBody>): Promise<AnalysisType> {
  return unwrap(await api.PATCH('/api/analysis-types/{key}', { params: { path: { key } }, body: patch }));
}
export async function validateAnalysisType(body: AnalysisTypeBody): Promise<string[]> {
  return unwrap(await api.POST('/api/analysis-types/validate', { body })).problems;
}
export async function listProjectAnalyses(projectId: string): Promise<AnalysisStaff[]> {
  return unwrap(await api.GET('/api/projects/{public_id}/analyses', { params: { path: { public_id: projectId } } }));
}
export async function planAnalysis(projectId: string, body: PlanAnalysisBody): Promise<AnalysisStaff> {
  return unwrap(await api.POST('/api/projects/{public_id}/analyses', { params: { path: { public_id: projectId } }, body }));
}
export async function getAnalysis(id: string): Promise<AnalysisStaff> {
  return unwrap(await api.GET('/api/analyses/{public_id}', { params: { path: { public_id: id } } }));
}
export async function patchAnalysis(id: string, patch: { name?: string; config?: Record<string, never> }): Promise<AnalysisStaff> {
  return unwrap(await api.PATCH('/api/analyses/{public_id}', { params: { path: { public_id: id } }, body: patch }));
}
const P = (id: string) => ({ params: { path: { public_id: id } } });
export async function removeAnalysis(id: string): Promise<void> {
  unwrap(await api.DELETE('/api/analyses/{public_id}', P(id)));
}
export async function bindSlot(id: string, role: string, data_object: string): Promise<AnalysisStaff> {
  return unwrap(await api.POST('/api/analyses/{public_id}/bind', { ...P(id), body: { role, data_object } }));
}
export async function unbindSlot(id: string, role: string): Promise<AnalysisStaff> {
  return unwrap(await api.POST('/api/analyses/{public_id}/unbind', { ...P(id), body: { role } }));
}
export async function launchAnalysis(id: string): Promise<AnalysisStaff> {
  return unwrap(await api.POST('/api/analyses/{public_id}/launch', P(id)));
}
export async function rerunAnalysis(id: string, body: RerunBody = { note: '' }): Promise<AnalysisStaff> {
  return unwrap(await api.POST('/api/analyses/{public_id}/rerun', { ...P(id), body }));
}
export type AttachOutputsBody = Omit<components['schemas']['AttachExtraOutputsRequest'], 'dry_run'>;
/**
 * Staff only: make the project's ready referenced objects outputs of the analysis. `outputs` stands in
 * for running a planned/ready analysis (→ review); `extra` adds them in any live state, internal unless
 * `deliverable`. `dry_run` answers with the analysis as it would be.
 */
export async function attachOutputs(id: string, mode: 'outputs' | 'extra', body: AttachOutputsBody, dryRun = false): Promise<AnalysisStaff> {
  if (mode === 'outputs') {
    const { data_objects, note } = body;
    return unwrap(await api.POST('/api/analyses/{public_id}/attach-outputs', { ...P(id), body: { data_objects, note, dry_run: dryRun } }));
  }
  return unwrap(await api.POST('/api/analyses/{public_id}/attach-extra-outputs', { ...P(id), body: { ...body, dry_run: dryRun } }));
}
export async function requestRerun(id: string, body: RerunBody = { note: '' }): Promise<AnalysisStaff> {
  return unwrap(await api.POST('/api/analyses/{public_id}/request-rerun', { ...P(id), body }));
}
export async function deliverAnalysis(id: string): Promise<AnalysisStaff> {
  return unwrap(await api.POST('/api/analyses/{public_id}/deliver', P(id)));
}
export async function cancelAnalysis(id: string, reason = ''): Promise<AnalysisStaff> {
  return unwrap(await api.POST('/api/analyses/{public_id}/cancel', { ...P(id), body: { reason } }));
}
export async function retypeAnalysis(id: string, type_key: string): Promise<AnalysisStaff> {
  return unwrap(await api.POST('/api/analyses/{public_id}/retype', { ...P(id), body: { type_key } }));
}
export async function projectChecklist(projectId: string): Promise<ChecklistRow[]> {
  return unwrap(await api.GET('/api/projects/{public_id}/checklist', { params: { path: { public_id: projectId } } }));
}
export async function candidates(projectId: string, kind?: string): Promise<Output[]> {
  return unwrap(await api.GET('/api/projects/{public_id}/candidates', { params: { path: { public_id: projectId }, query: { kind } } }));
}

export function useAnalysisTypes() {
  return useQuery({ queryKey: ['analysis-types'], queryFn: listAnalysisTypes, staleTime: 30_000 });
}
export function useAnalysisType(key: string | undefined) {
  return useQuery({ queryKey: ['analysis-type', key], queryFn: () => getAnalysisType(key!), enabled: !!key && key !== 'new' });
}
export function useProjectAnalyses(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['analyses', projectId],
    queryFn: () => listProjectAnalyses(projectId!),
    enabled: !!projectId && enabled,
    refetchInterval: (q) => (q.state.data?.some((a) => isProcessing(a.state) || a.client_status === 'processing') ? 5000 : false),
  });
}
export function useAnalysis(id: string | undefined) {
  return useQuery({
    queryKey: ['analysis', id],
    queryFn: () => getAnalysis(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && (isProcessing(q.state.data.state) || q.state.data.client_status === 'processing') ? 4000 : false),
  });
}
export function useChecklist(projectId: string | undefined, enabled = true) {
  return useQuery({ queryKey: ['checklist', projectId], queryFn: () => projectChecklist(projectId!), enabled: !!projectId && enabled });
}
export function useCandidates(projectId: string | undefined, kind: string | undefined) {
  return useQuery({ queryKey: ['candidates', projectId, kind], queryFn: () => candidates(projectId!, kind), enabled: !!projectId && !!kind });
}

export function useInvalidateAnalyses() {
  const qc = useQueryClient();
  return (analysisId?: string, projectId?: string) => {
    void qc.invalidateQueries({ queryKey: ['analyses'] });
    void qc.invalidateQueries({ queryKey: ['checklist'] });
    void qc.invalidateQueries({ queryKey: ['objects'] });
    void qc.invalidateQueries({ queryKey: ['projects'] });
    void qc.invalidateQueries({ queryKey: ['runs'] });
    // Cancelling hides the reports over the analysis's outputs.
    void qc.invalidateQueries({ queryKey: ['reports'] });
    void qc.invalidateQueries({ queryKey: ['report'] });
    if (analysisId) void qc.invalidateQueries({ queryKey: ['analysis', analysisId] });
    if (projectId) void qc.invalidateQueries({ queryKey: ['project', projectId] });
  };
}
/** One mutation hook for every staff action on an analysis; invalidates what the action touches. */
export function useAnalysisAction<TArgs extends unknown[]>(fn: (id: string, ...args: TArgs) => Promise<AnalysisStaff>) {
  const invalidate = useInvalidateAnalyses();
  return useMutation({
    mutationFn: ({ id, args }: { id: string; args: TArgs }) => fn(id, ...args),
    onSuccess: (a) => invalidate(a.public_id),
  });
}
export function useAnalysisTypeMutations() {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['analysis-types'] });
    void qc.invalidateQueries({ queryKey: ['analysis-type'] });
  };
  return {
    create: useMutation({ mutationFn: createAnalysisType, onSuccess: done }),
    update: useMutation({ mutationFn: ({ key, patch }: { key: string; patch: Partial<AnalysisTypeBody> }) => updateAnalysisType(key, patch), onSuccess: done }),
  };
}
